import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-surface dark:bg-surface-dark py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Please read these terms carefully before using Vegetablah.com.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Acceptance of Terms */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Acceptance of Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              By accessing and using Vegetablah.com ("the Service"), you accept and agree to be bound by the terms
              and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              These Terms of Service apply to all users of the Service, including without limitation users who are
              browsers, vendors, customers, merchants, and/or contributors of content.
            </p>
          </CardContent>
        </Card>

        {/* Description of Service */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Description of Service</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              Vegetablah.com is a comprehensive health and wellness tracking platform that provides:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
              <li>Food intake logging and nutritional analysis</li>
              <li>Health metrics tracking and monitoring</li>
              <li>Personal statistics and correlation analysis</li>
              <li>Integration with third-party fitness devices and services</li>
              <li>Educational content and articles about health and nutrition</li>
            </ul>
          </CardContent>
        </Card>

        {/* User Accounts */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">User Accounts and Registration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Account Creation</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  To use certain features of the Service, you must register for an account. When you register,
                  you agree to provide accurate, current, and complete information and maintain and update
                  your information as necessary.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Account Security</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  You are responsible for safeguarding your account credentials and for all activities that occur
                  under your account. You must immediately notify us of any unauthorized use of your account.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Responsibilities */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">User Responsibilities</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              You agree to use the Service only for lawful purposes and in accordance with these Terms:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
              <li>Provide accurate and truthful information when using the Service</li>
              <li>Not use the Service for any illegal or unauthorized purpose</li>
              <li>Not attempt to gain unauthorized access to any part of the Service</li>
              <li>Not interfere with or disrupt the Service or servers</li>
              <li>Not use automated systems to access the Service without permission</li>
              <li>Respect the privacy and rights of other users</li>
            </ul>
          </CardContent>
        </Card>

        {/* Content and Conduct */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Content and User Conduct</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">User-Generated Content</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  You retain ownership of content you submit to the Service. However, by submitting content,
                  you grant us a worldwide, non-exclusive, royalty-free license to use, display, and distribute
                  your content in connection with the Service.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Prohibited Content</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  You agree not to post content that is illegal, harmful, threatening, abusive, harassing,
                  defamatory, vulgar, obscene, invasive of another's privacy, or otherwise objectionable.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Intellectual Property</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              The Service and its original content, features, and functionality are and will remain the exclusive
              property of Vegetablah.com and its licensors. The Service is protected by copyright, trademark,
              and other laws.
            </p>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              You may not duplicate, copy, or reuse any portion of the Service's code or visual design elements
              without express written permission from us.
            </p>
          </CardContent>
        </Card>

        {/* Termination */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Termination</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              We may terminate or suspend your account and access to the Service immediately, without prior notice
              or liability, for any reason, including without limitation if you breach these Terms.
            </p>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Upon termination, your right to use the Service will cease immediately. All provisions of these Terms
              which by their nature should survive termination shall survive, including ownership provisions,
              warranty disclaimers, and limitations of liability.
            </p>
          </CardContent>
        </Card>

        {/* Disclaimer of Warranties */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Disclaimer of Warranties</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. We make no representations or warranties
              of any kind, express or implied, as to the operation of the Service or the information, content, or
              materials included therein.
            </p>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              You expressly agree that your use of the Service is at your sole risk. We do not warrant that the Service
              will be uninterrupted or error-free, and we will not be liable for any interruptions or errors.
            </p>
          </CardContent>
        </Card>

        {/* Limitation of Liability */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              In no event shall Vegetablah.com, its directors, employees, partners, agents, suppliers, or affiliates
              be liable for any indirect, incidental, special, consequential, or punitive damages, including without
              limitation, loss of profits, data, use, goodwill, or other intangible losses.
            </p>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Our total liability shall not exceed the amount paid by you for the Service in the twelve months
              preceding the claim.
            </p>
          </CardContent>
        </Card>

        {/* Indemnification */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Indemnification</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              You agree to defend, indemnify, and hold harmless Vegetablah.com and its licensee and licensors,
              and their employees, contractors, agents, officers and directors, from and against any and all claims,
              damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to
              attorney's fees), resulting from or arising out of a) your use and access of the Service, by you or
              any person using your account and password, or b) a breach of these Terms.
            </p>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Governing Law</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              These Terms shall be interpreted and governed by the laws of the jurisdiction in which Vegetablah.com
              operates, without regard to its conflict of law provisions. Our failure to enforce any right or provision
              of these Terms will not be considered a waiver of those rights.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Changes to Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision
              is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes
              a material change will be determined at our sole discretion. Your continued use of the Service after any
              such changes constitutes your acceptance of the new Terms.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="text-center">
          <Card>
            <CardContent className="py-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Questions About These Terms?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2 text-gray-600 dark:text-gray-300">
                <p><strong>Email:</strong> support@vegetablah.com</p>
                <p><strong>Address:</strong> 18902 224th Ave Ct E, Orting, WA 98360</p>
              </div>
              <div className="mt-6">
                <a
                  href="/contact"
                  className="inline-block bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  Contact Us
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
